import { useApiGetCountPenilaianTahunanDinas } from 'api/kinerja/kepala-sekolah/dinas/penilaian-tahunan'
import { useApiGetCountPenilaianTahunanDinasTabGuru } from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian-tahunan'
import useRiwayatTahunanAccessibility from 'app/KinerjaShared/hooks/useRiwayatTahunanAccessibility'
import { DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED } from 'app/KinerjaDinasPage/constants'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { TResponsePenilaianTahunanCount } from 'types/kinerja-dinas-penilaian'

type TResponse = {
  // Common
  isWithinAllowedRange: boolean
  isAfterAllowedDate: boolean
  isClickable: boolean

  // Kepala Sekolah
  isCountLoadingKs: boolean
  isCountErrorKs: boolean
  isCountSuccessKs: boolean
  itemCountKs: TResponsePenilaianTahunanCount['data']
  metasCountKs: TResponsePenilaianTahunanCount['meta']
  refetchCountKs: () => void

  // Guru
  isCountLoadingGuru: boolean
  isCountErrorGuru: boolean
  isCountSuccessGuru: boolean
  itemCountGuru: TResponsePenilaianTahunanCount['data']
  metasCountGuru: TResponsePenilaianTahunanCount['meta']
  refetchCountGuru: () => void

  // Combined
  isLoading: boolean
  isBothFetched: boolean
  isSuccess: boolean
  countAll: number
  countNotSent: number
  countSent: number
  isAllSent: boolean
}

// Get the current date
const today = new Date()

const usePenetapanTahunanCountDinas = (): TResponse => {
  const { periodYear } = usePeriodContext()

  const {
    isLoading: isLoadingGetCommonConfig,
    isFetched: isFetchedGetCommonConfig,
    isSuccess: isSuccessGetCommonConfig,
    isWithinAllowedRange,
    isAfterAllowedDate,
  } = useRiwayatTahunanAccessibility({ today })

  // Kepala Sekolah
  const {
    isLoading: isCountLoadingKs,
    isError: isCountErrorKs,
    isSuccess: isCountSuccessKs,
    isFetched: isCountFetchedKs,
    data: { data: itemCountKs, meta: metasCountKs } = {},
    refetch: refetchCountKs,
  } = useApiGetCountPenilaianTahunanDinas(
    { year: periodYear },
    { enabled: isWithinAllowedRange }
  )

  // Guru
  const {
    isLoading: isCountLoadingGuru,
    isError: isCountErrorGuru,
    isSuccess: isCountSuccessGuru,
    isFetched: isCountFetchedGuru,
    data: { data: itemCountGuru, meta: metasCountGuru } = {},
    refetch: refetchCountGuru,
  } = useApiGetCountPenilaianTahunanDinasTabGuru(
    { year: periodYear },
    { enabled: isWithinAllowedRange && DINAS_PENETAPAN_TAHUNAN_GURU_ENABLED }
  )

  // Combine the results
  const countAll = (itemCountKs?.all || 0) + (itemCountGuru?.all || 0)
  const countNotSent =
    (itemCountKs?.belumDikirim || 0) + (itemCountGuru?.belumDikirim || 0)
  const countSent =
    (itemCountKs?.sudahDikirim || 0) + (itemCountGuru?.sudahDikirim || 0)
  const isAllSent = countAll === countSent && countAll > 0

  const isLoading =
    isLoadingGetCommonConfig || isCountLoadingKs || isCountLoadingGuru
  const isSuccess =
    isSuccessGetCommonConfig && isCountSuccessKs && isCountSuccessGuru
  const isBothFetched =
    isFetchedGetCommonConfig && isCountFetchedKs && isCountFetchedGuru

  return {
    // Common
    isWithinAllowedRange,
    isAfterAllowedDate,
    isClickable: countAll > 0,

    // Kepala Sekolah
    isCountLoadingKs,
    isCountErrorKs,
    isCountSuccessKs,
    itemCountKs,
    metasCountKs,
    refetchCountKs,

    // Guru
    isCountLoadingGuru,
    isCountErrorGuru,
    isCountSuccessGuru,
    itemCountGuru,
    metasCountGuru,
    refetchCountGuru,

    // Combined
    isLoading,
    isBothFetched,
    isSuccess,
    countAll,
    countNotSent,
    countSent,
    isAllSent,
  }
}

export default usePenetapanTahunanCountDinas
